import {
  getFindPersonDetailsQueryKey,
  useDeleteContactCard as useDeleteContactCardMutation,
} from '@api';
import { useInvalidateQuery, useNotification } from '@common';
import { modals } from '@mantine/modals';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

const TRANS_KEY_MODAL = 'COMPANY_LINKS.CONTACT_CARD.WITHDRAW_CONFIRM_MODAL';
const TRANS_KEY_NOTIFICATION = 'NOTIFICATIONS.WITHDRAW_CONTACT_CARD';

type UseWithdrawContactCardArgsType = {
  personId: string;
  onClose: () => void;
};

type UseWithdrawContactCardReturnType = {
  onWithdraw: (companyLinkId: string) => void;
};

export const useWithdrawContactCard = ({
  personId,
  onClose,
}: UseWithdrawContactCardArgsType): UseWithdrawContactCardReturnType => {
  const { t } = useTranslation('personDetailsGeneral');
  const { mutateAsync } = useDeleteContactCardMutation();
  const { showError, showSuccess } = useNotification();
  const { invalidateQuery } = useInvalidateQuery(
    getFindPersonDetailsQueryKey(personId)
  );

  const withdrawContactCard = async (companyLinkId: string): Promise<void> => {
    try {
      await mutateAsync({ companyLinkId });
      await invalidateQuery();
      showSuccess({
        title: `${TRANS_KEY_NOTIFICATION}.SUCCESS.TITLE`,
        message: `${TRANS_KEY_NOTIFICATION}.SUCCESS.MESSAGE`,
      });
      onClose();
    } catch (error) {
      if (isAxiosError(error)) {
        showError({
          title: `${TRANS_KEY_NOTIFICATION}.ERROR.TITLE`,
          message: `${TRANS_KEY_NOTIFICATION}.ERROR.MESSAGE`,
          error,
        });
      }
    }
  };

  const onWithdraw = (companyLinkId: string): void => {
    modals.openConfirmModal({
      title: t(`${TRANS_KEY_MODAL}.TITLE`),
      labels: {
        confirm: t(`${TRANS_KEY_MODAL}.CONFIRM_BUTTON`),
        cancel: t('CANCEL_BUTTON', { ns: 'common' }),
      },
      confirmProps: {
        color: 'red',
      },
      onConfirm: () => {
        void withdrawContactCard(companyLinkId);
      },
    });
  };

  return { onWithdraw };
};
